
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import ActionButton from '@/components/ActionButton.vue';
import {Fullscreen} from '@shopify/app-bridge/actions';
import { isShopifyEmbedded } from '@shopify/app-bridge/utilities';
import {StoreModel} from '@/models/store';
import OnboardCompleteConfirm from '@/components/OnboardCompleteConfirm.vue';
import { EventBus } from '@/main';


@Component({
  components: {
    ActionButton,
    OnboardCompleteConfirm,
  },
})
export default class AppBanner extends Vue {
  @Prop({default: null})
  public app!: any;
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public isFullscreenMode: boolean = false;
  public showReportIssue: boolean = false;
  public dialogNotifyStoreDemo: boolean = false;
  public isDemoStore: boolean = false;
  public demoStoreName: string = 'assisty-demo.myshopify.com';
  public shopifyFlowBannerDismissed: boolean = false;

  public created() {
    this.showReportIssue = localStorage.getItem('showReportIssue') !== 'false';
    if (this.store.shopName === this.demoStoreName || window.location.hostname === 'demo.assisty.ai') {
      this.isDemoStore = true;
      this.dialogNotifyStoreDemo = true;
    }
    // Load resource counts so syncProgress formula has all data
    if (!this.store.isAthenaFinished) {
      this.loadResourceCounts();
    }
  }

  private async loadResourceCounts() {
    try {
      const calls: Promise<any>[] = [];
      if (!this.store.locationCount?.resource) calls.push(this.store.resourceCount('locations'));
      if (!this.store.customCollection?.resource) calls.push(this.store.resourceCount('custom_collections'));
      if (!this.store.smartCollection?.resource) calls.push(this.store.resourceCount('smart_collections'));
      if (!this.store.productCount?.resource) calls.push(this.store.resourceCount('products'));
      if (!this.store.orderCount?.resource) calls.push(this.store.resourceCount('orders'));
      if (calls.length > 0) await Promise.all(calls);
    } catch (e) {
      console.warn('Failed to load resource counts for progress calculation:', e);
    }
  }

  public viewImportingStatusPage() {
    this.$router.push('/importing-status');
  }

  public viewDataImportingPage() {
    this.$router.push('/data-importing');
  }

  public openChat() {
    const message: string = 'I have encountered an issue while using the app and would like to report it.';
    // @ts-ignore: Unreachable code error
    this.$crisp.do('chat:open');
    // @ts-ignore: Unreachable code error
    this.$crisp.do('message:send', ['text', message]);
  }

  public doFullscreen() {
    const fullscreen = Fullscreen.create(this.app);
    fullscreen.dispatch(Fullscreen.Action.ENTER);
    this.isFullscreenMode = true;
    this.showReportIssue = false;
    localStorage.setItem('showReportIssue', 'false');
  }

  public exitFullscreen() {
    const fullscreen = Fullscreen.create(this.app);
    if (this.isFullscreenMode) {
      fullscreen.dispatch(Fullscreen.Action.EXIT);
      this.isFullscreenMode = false;
    }
    this.isFullscreenMode = false;
  }

  public get isEmbeddedMode() {
    return isShopifyEmbedded();
  }

  private resourceProg(count: { modelCount: number; apiCount: number; resource?: string } | null): number {
    if (!count) return 0;
    // resource is '' before API response — not loaded yet
    if (!count.resource) return 0;
    if (count.apiCount <= 0) return 100;
    return count.modelCount >= count.apiCount ? 100 : (count.modelCount / count.apiCount) * 100;
  }

  get isFreePlan(): boolean {
    return this.store.currentPackage?.price === 0;
  }

  get salesFullHistoryProgress(): number {
    const s = this.store;
    if (s.isSalesOrderOlapFinished) return 100;
    if (!s.isSalesOrderOlapReady) return 0;
    // Interpolate between ready (1 year) and finished (full history)
    for (const etlStatus of s.etlStatuses.items) {
      if (etlStatus.resource === 'sales_order_variant_olap') {
        const startTime = etlStatus.startTime ? new Date(etlStatus.startTime) : null;
        const limitStartTime = etlStatus.limitStartTime ? new Date(etlStatus.limitStartTime) : null;
        if (!startTime || !limitStartTime || !s.installedAt) return 0;
        const installedAt = new Date(s.installedAt).setHours(0, 0, 0, 0);
        const readyDate = new Date(installedAt);
        readyDate.setFullYear(readyDate.getFullYear() - 1);
        const readyTs = readyDate.getTime();
        const limitTs = limitStartTime.getTime();
        const currentTs = startTime.getTime();
        if (readyTs <= limitTs) return 100;
        const totalSpan = readyTs - limitTs;
        const completedSpan = readyTs - currentTs;
        return Math.min(Math.max((completedSpan / totalSpan) * 100, 0), 100);
      }
    }
    return 0;
  }

  get hasMoreThanOneYearHistory(): boolean {
    const s = this.store;
    if (!s.firstOrderAt || !s.installedAt) return false;
    const firstOrderTime = new Date(s.firstOrderAt).getTime();
    const installedAt = new Date(s.installedAt).setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(installedAt);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return firstOrderTime < oneYearAgo.getTime();
  }

  get syncProgress(): number {
    const s = this.store;
    const locationProg = this.resourceProg(s.locationCount);
    const customApi = s.customCollection?.apiCount || 0;
    const smartApi = s.smartCollection?.apiCount || 0;
    const customModel = s.customCollection?.modelCount || 0;
    const smartModel = s.smartCollection?.modelCount || 0;
    const collectionApi = customApi + smartApi;
    const collectionModel = Math.min(customModel + smartModel, collectionApi);
    const collectionProg = collectionApi > 0 ? (collectionModel / collectionApi) * 100 : 100;
    const productProg = this.resourceProg(s.productCount);
    const orderProg = this.resourceProg(s.orderCount);
    const rawImport = (locationProg + collectionProg + productProg + orderProg) / 4;
    const salesReady = s.isSalesOrderOlapReady ? 100 : 0;
    const salesFinished = this.salesFullHistoryProgress;

    if (this.isFreePlan) {
      // Free plan: no inventory, only 6 months sales — raw imports 70%, sales ready 30%
      return Math.min((rawImport * 0.7) + (salesReady / 100 * 30), 100);
    }
    const inventory = s.isInventoryLevelsReady ? 100 : 0;
    if (this.hasMoreThanOneYearHistory) {
      return Math.min((rawImport * 0.6) + (salesReady / 100 * 20) + (salesFinished / 100 * 10) + (inventory / 100 * 10), 100);
    }
    return Math.min((rawImport * 0.6) + (salesReady / 100 * 30) + (inventory / 100 * 10), 100);
  }

  get showSubscriptionButton(): boolean {
    return (
      this.store.currentPackage &&
      this.store.currentPackage.name !== 'Free' &&
      this.store.isAthenaFinished &&
      !this.isDemoStore
    );
  }

  get showShopifyFlowBanner(): boolean {
    return (
      !this.shopifyFlowBannerDismissed &&
      this.store.isAthenaFinished &&
      !this.isDemoStore
    );
  }

  public dismissShopifyFlowBanner() {
    this.shopifyFlowBannerDismissed = true;
  }

  public viewSubscription() {
    this.$router.push({
      path: '/subscription',
    });
  }
}
